import { ReactNode, useState } from 'react';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { executeRecaptcha, Typography } from '@alltrails/core';
import type Context from '@alltrails/core/types/Context';
import Button from '@alltrails/denali/components/Button';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Checkbox from '@alltrails/denali/components/Checkbox';
import TextInput from '@alltrails/denali/components/TextInput';
import { ApiError, post } from '@alltrails/shared/api';
import logSignUpFailed from '@alltrails/amplitude/events/logSignUpFailed';
import { useSelector } from 'react-redux';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import getClientId from '../../utils/getClientId';
import { authSuccessCallback } from '../../utils/AuthHandlers';
import type LoginResponse from '../../types/LoginResponse';
import validateUserForm from '../../utils/validateUserForm';
import styles from './styles.module.scss';

const LABELS = defineMessages({
  SIGN_UP: {
    defaultMessage: 'Sign up'
  },
  FIRST_NAME: {
    defaultMessage: 'First name'
  },
  LAST_NAME: {
    defaultMessage: 'Last name'
  },
  EMAIL_ADDRESS: {
    defaultMessage: 'Email address'
  },
  PASSWORD: {
    defaultMessage: 'Password'
  }
});

type Props = {
  autoFocus?: boolean;
  referralLink?: string;
  handleRegistrationSuccess: (response: LoginResponse) => void;
  userEmailSubscribed?: boolean;
  grantType?: string | null;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6514%3A101082
export default function RegisterForm({ autoFocus = false, referralLink, handleRegistrationSuccess, userEmailSubscribed, grantType }: Props) {
  const features = useFeatures();

  const {
    formattedDefaultMessages: { SIGN_UP, FIRST_NAME, LAST_NAME, EMAIL_ADDRESS, PASSWORD }
  } = useFormatMessage(LABELS);

  const intl = useIntl();
  const countryId = useSelector((state: { context: Context }) => state.context?.countryId);
  const [serverError, setServerError] = useState<ReactNode>();

  const [state, setState] = useState<{
    userEmailSubscribed: boolean;
    userRegLastName: string;
    userRegEmail: string;
    userRegPassword: string;
    userRegFirstName: string;
    submitting: boolean;
    showErrorsOnType: boolean;
  }>({
    userRegEmail: '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed: Boolean(userEmailSubscribed),
    submitting: false,
    showErrorsOnType: false
  });

  const [errors, setErrors] = useState<{ userRegEmail?: string; userRegPassword?: string; userRegFirstName?: string; userRegLastName?: string }>({});

  function showEmailSubscribed() {
    return countryId !== 313;
  }

  function handleSubscribedChange() {
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function handleChange(name: string, value: string) {
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors, intl).errors);
    }

    setState({ ...state, [name]: value });
  }

  async function submitRegister(e: React.KeyboardEvent<HTMLDivElement> | React.FormEvent) {
    e.preventDefault();

    if (state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors, intl).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });
      return;
    }

    setState({ ...state, submitting: true });

    try {
      let recaptcha;

      // on alpha we disable recaptcha for Cypress testing
      if (features.includes('recaptcha_user_signup_login')) {
        recaptcha = await executeRecaptcha('userSignupLogin');
      }

      const response = await post<LoginResponse>('/identity/auth/register', {
        client_id: getClientId(),
        ...(grantType && { grant_type: grantType }),
        user: {
          first_name: state.userRegFirstName,
          last_name: state.userRegLastName,
          email: state.userRegEmail,
          password: state.userRegPassword,
          create_session: true,
          subscribed: state.userEmailSubscribed,
          recaptcha,
          referral_code: referralLink
        }
      });

      authSuccessCallback(response, 'email', response.is_collab_list_invite);

      setErrors({});

      setState({
        userRegEmail: '',
        userRegPassword: '',
        userRegFirstName: '',
        userRegLastName: '',
        userEmailSubscribed: Boolean(userEmailSubscribed),
        submitting: false,
        showErrorsOnType: false
      });

      setServerError(null);

      handleRegistrationSuccess(response);
    } catch (e: any) {
      if (e instanceof ApiError && e.data?.errors?.length > 0) {
        if (e.data.errors[0].target === 'email') {
          setErrors({ ...errors, userRegEmail: e.data.errors[0].message });
        }
        setServerError(e.data.errors[0].message);
      } else {
        setServerError(<FormattedMessage defaultMessage="Error, please try again" />);
      }

      logSignUpFailed({
        authentication_type: 'email',
        failure_type: e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status
      });
    } finally {
      setState({ ...state, submitting: false });
    }
  }

  return (
    <form className={styles.form} onSubmit={submitRegister}>
      <TextInput
        autoFocus={autoFocus}
        name="userRegFirstName"
        errorText={errors.userRegFirstName}
        value={state.userRegFirstName}
        labelText={FIRST_NAME}
        placeholder={FIRST_NAME}
        onChange={(value: string) => {
          handleChange('userRegFirstName', value);
        }}
        data-testid="registrationForm-firstNameTextField"
      />
      <TextInput
        name="userRegLastName"
        errorText={errors.userRegLastName}
        value={state.userRegLastName}
        labelText={LAST_NAME}
        placeholder={LAST_NAME}
        onChange={(value: string) => {
          handleChange('userRegLastName', value);
        }}
        data-testid="registrationForm-lastNameTextField"
      />
      <TextInput
        name="userRegEmail"
        errorText={errors.userRegEmail}
        value={state.userRegEmail}
        placeholder={EMAIL_ADDRESS}
        labelText={EMAIL_ADDRESS}
        onChange={(value: string) => {
          if (serverError) {
            setServerError(null);
          }
          if (errors.userRegEmail) {
            setErrors({ userRegEmail: '' });
          }
          handleChange('userRegEmail', value);
        }}
        type="email"
        data-testid="registrationForm-emailTextField"
      />
      <TextInput
        name="userRegPassword"
        errorText={errors.userRegPassword}
        value={state.userRegPassword}
        placeholder={PASSWORD}
        labelText={PASSWORD}
        onChange={(value: string) => {
          handleChange('userRegPassword', value);
        }}
        type="password"
        data-testid="registrationForm-passwordTextField"
      />
      {serverError && <div className={`${styles.serverError} ${styles.center}`}>{serverError}</div>}
      <div className={styles.bottom}>
        <Button text={SIGN_UP} disabled={state.submitting} testId="register-form-sign-up" type="submit" variant="primary" width="full" />
        {showEmailSubscribed() && (
          <Checkbox
            className={styles.subscribedContainer}
            id="emailSubscribed"
            testId="checkbox-emailSubscribed"
            size="sm"
            onChange={handleSubscribedChange}
            selected={state.userEmailSubscribed}
            labelElement={
              <Typography variant="text200">
                <FormattedMessage defaultMessage="Get helpful product tips and special offers" />
              </Typography>
            }
          />
        )}
      </div>
    </form>
  );
}
